
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Loader2 } from "lucide-react";
import { FormField } from "./BaseForm";
import { ContactDetailsFormData, FormProps } from "@/types/forms";

interface ContactDetailsFormProps extends FormProps<ContactDetailsFormData> {
  onBack?: () => void;
  isSubmitting?: boolean;
}

const ContactDetailsForm = ({ onSubmit, initialData = {}, onBack, isSubmitting = false }: ContactDetailsFormProps) => {
  const [formData, setFormData] = useState<ContactDetailsFormData>({
    name: initialData.name || "",
    email: initialData.email || "",
    phone: initialData.phone || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.phone) newErrors.phone = "Phone number is required";
    
    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const updateField = <K extends keyof ContactDetailsFormData>(field: K, value: ContactDetailsFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Thank you!</h3>
        <p className="text-gray-600">We'll be in touch shortly with your quote. Please provide your contact details:</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <FormField label="Your Name" required>
          <Input
            placeholder="Enter your full name"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
          />
          {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
        </FormField>

        <FormField label="Your Email" required>
          <Input
            type="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={(e) => updateField('email', e.target.value)}
          />
          {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
        </FormField>

        <FormField label="Your Contact Number" required>
          <Input
            type="tel"
            placeholder="Enter your phone number"
            value={formData.phone}
            onChange={(e) => updateField('phone', e.target.value)}
          />
          {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
        </FormField>

        <div className="flex gap-4">
          {onBack && (
            <Button type="button" variant="outline" onClick={onBack} className="flex-1">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
          <Button type="submit" className="flex-1" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Quote Request"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ContactDetailsForm;
